"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "motion/react";
import { ImagePlus, UploadCloud, X, RefreshCw } from "lucide-react";
import { cn } from "../lib/cn";
import { springs, transitions } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Image } from "../primitives/Image";
import { PixelCard } from "../primitives/PixelCard";
import { Spinner } from "../primitives/Spinner";

/**
 * ImageUploader — drag-drop / click image input with object-URL preview,
 * type + size validation, and a pixel-reveal transition into the preview.
 *
 * Consolidates four hand-rolled image pickers across the platform (the two
 * unified logo wells in `CreateStudioModal` / `CreateWorkspaceModal`, plus
 * fe-react's `ProfileHero` avatar and `CoverImageUpload` cover surface) into
 * one accessible, leak-free seam. The component owns the parts every copy got
 * subtly wrong: the object URL is created **and revoked** internally (on
 * unmount and on every replace), the hidden `<input>` is reset to `""` after
 * each select so re-picking the same file re-fires `onChange`, and validation
 * rejections are reported **outward** via `onReject` rather than toasted from
 * inside — the consumer decides what to persist and what to surface.
 *
 * Drag-drop is native (`onDragEnter` / `onDragOver` / `onDragLeave` / `onDrop`
 * + a counter to survive child-element drag bubbling) plus a hidden
 * `<input type="file">` for click / keyboard, so the component ships with
 * **zero** new dependencies — it composes the existing `Image` primitive for
 * the rendered preview (loading skeleton + `onError` fallback for free) and
 * `PixelCard` (controlled) for the reveal.
 *
 * Two variants:
 * - `avatar`  — small square / circle well with an initial-letter fallback
 *   (the unified modals' 44px logo well). Drag-drop still works.
 * - `dropzone` — the `min-h-[220px]` framed cover surface with a dashed
 *   border, title / subtitle / hint copy, and Replace / Remove buttons.
 *
 * File value is hybrid controlled / uncontrolled: pass `value` (a `File`, an
 * existing remote URL string, or `null`) + `onChange`, or omit both and let
 * the component hold its own state seeded by `defaultValue`.
 *
 * Persistence seam: pass `upload(file) => Promise<string>` to drive the
 * uploading → done lifecycle (the component dims + spins, then resolves to the
 * returned URL); omit it and the component stays a pure object-URL preview.
 *
 * Motion: the empty ↔ preview swap cross-fades through `AnimatePresence`, the
 * preview resolves via `PixelCard`'s controlled pixel-reveal, the dropzone
 * tints + lifts on drag-enter, and the surface dims while uploading. Every
 * transform is gated on `useReducedMotion()` with an opacity-only fallback;
 * `PixelCard` and `Image` already self-collapse their motion under the same
 * media query.
 *
 * SSR-safe (no `window` / `document` at module scope; object URLs are created
 * inside effects / event handlers). Light theme only.
 *
 * @example Avatar logo well (controlled, the unified-modal swap)
 *   const [logo, setLogo] = React.useState<File | null>(null);
 *   <ImageUploader variant="avatar" value={logo} onChange={setLogo} name="Acme" />
 *
 * @example Cover dropzone with persistence + rejection toast delegated out
 *   <ImageUploader
 *     variant="dropzone"
 *     accept={["image/jpeg", "image/png", "image/webp", "image/svg+xml"]}
 *     upload={(file) => api.uploadCover(file)}
 *     onReject={(rejections) => toast.error(rejections[0]?.message)}
 *     hint="JPG, PNG, WebP, SVG • Max 5MB"
 *   />
 */

/* ============================================================================
 * Types
 * ========================================================================= */

/** Why a file was refused by the built-in validation. */
export type ImageRejectionCode = "file-invalid-type" | "file-too-large";

export interface ImageRejection {
  /** The rejected file. */
  file: File;
  /** Machine-readable reason (mirrors react-dropzone's reason codes). */
  code: ImageRejectionCode;
  /** Human-readable message the consumer can toast verbatim. */
  message: string;
}

/** The current upload-driven lifecycle phase. */
export type ImageUploaderStatus = "idle" | "uploading" | "done" | "error";

export const imageUploaderVariants = cva(
  cn(
    "group/uploader relative isolate overflow-hidden",
    "transition-colors duration-180 ease-smooth-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "ring-offset-background",
  ),
  {
    variants: {
      variant: {
        // Small logo / avatar well. Bordered, solid surface, initial-letter
        // fallback when empty.
        avatar: "inline-flex items-center justify-center border border-input bg-muted text-muted-foreground",
        // Framed cover surface. Dashed border, taller min-height, copy stack.
        dropzone: "flex w-full flex-col items-center justify-center border border-dashed border-input bg-background text-center",
      },
      shape: {
        square: "",
        circle: "rounded-full",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
      },
      dragging: {
        true: "border-primary bg-primary/[0.04]",
        false: "",
      },
      hasError: {
        true: "border-destructive bg-destructive/[0.04]",
        false: "",
      },
      disabled: {
        true: "pointer-events-none opacity-50",
        false: "",
      },
    },
    compoundVariants: [
      // Square radius scales with size; circle is handled by the `shape` row.
      { variant: "avatar", shape: "square", size: "sm", className: "h-11 w-11 rounded-lg" },
      { variant: "avatar", shape: "square", size: "md", className: "h-16 w-16 rounded-xl" },
      { variant: "avatar", shape: "square", size: "lg", className: "h-24 w-24 rounded-2xl" },
      { variant: "avatar", shape: "circle", size: "sm", className: "h-11 w-11" },
      { variant: "avatar", shape: "circle", size: "md", className: "h-16 w-16" },
      { variant: "avatar", shape: "circle", size: "lg", className: "h-24 w-24" },
      { variant: "dropzone", size: "sm", className: "min-h-[160px] gap-2 rounded-xl p-5" },
      { variant: "dropzone", size: "md", className: "min-h-[220px] gap-3 rounded-2xl p-6" },
      { variant: "dropzone", size: "lg", className: "min-h-[280px] gap-4 rounded-2xl p-8" },
    ],
    defaultVariants: {
      variant: "dropzone",
      shape: "square",
      size: "md",
      dragging: false,
      hasError: false,
      disabled: false,
    },
  },
);

type RootVariantProps = Pick<VariantProps<typeof imageUploaderVariants>, "variant" | "shape" | "size">;

export interface ImageUploaderProps
  extends Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "onChange" | "defaultValue" | "onError" | "children" | "title"
    >,
    RootVariantProps {
  /**
   * Controlled file value. `File` = freshly picked; `string` = an existing
   * remote URL (e.g. an already-saved logo) shown as the initial preview;
   * `null` = empty. Pair with `onChange`.
   */
  value?: File | string | null;
  /**
   * Uncontrolled initial value. Same shape as `value`; ignored once `value`
   * is provided. Mirrors `<input defaultValue>` semantics.
   */
  defaultValue?: File | string | null;
  /** Fires with the next file on select / drop, and `null` on remove. */
  onChange?: (file: File | null) => void;
  /**
   * Accepted MIME types. Default is the **no-SVG** set (ProfileHero's stricter
   * policy). Pass the SVG-inclusive set to match the wizard cover surface.
   */
  accept?: readonly string[];
  /** Max file size in bytes. Default `5 * 1024 * 1024` (5MB). */
  maxSizeBytes?: number;
  /**
   * Called with every file that failed validation (wrong type / too large)
   * so the consumer can toast. The component never toasts internally.
   */
  onReject?: (rejections: ImageRejection[]) => void;
  /**
   * Persistence seam. When provided, a picked file drives uploading → done
   * and the resolved URL becomes the visible preview (the ProfileHero
   * preload-then-swap, without re-touching the DOM). When absent the
   * component stays a pure object-URL preview.
   */
  upload?: (file: File) => Promise<string>;
  /** Mirrors the uploading flag outward (matches `onUploadStatusChange`). */
  onUploadingChange?: (uploading: boolean) => void;
  /** Heading copy (dropzone variant). Default "Drop an image here". */
  title?: React.ReactNode;
  /** Sub-copy under the title (dropzone variant). Default "or click to browse". */
  subtitle?: React.ReactNode;
  /** Constraint hint (dropzone variant), e.g. "JPG, PNG, WebP • Max 5MB". */
  hint?: React.ReactNode;
  /** Name used for the avatar initial-letter fallback + the preview `alt`. */
  name?: string;
  /** Accessible label for the control. Default "Upload image". */
  "aria-label"?: string;
  /** Replace the default rendered preview entirely. Receives the resolved src. */
  renderPreview?: (src: string) => React.ReactNode;
  /** Disable all interaction. */
  disabled?: boolean;
}

/* ============================================================================
 * Constants + helpers
 * ========================================================================= */

const DEFAULT_ACCEPT: readonly string[] = ["image/jpeg", "image/png", "image/webp"];
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

const initialOf = (name?: string): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "";
  return trimmed[0]?.toUpperCase() ?? "";
};

const formatMaxSize = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  // Whole numbers read cleaner ("5MB"); keep one decimal otherwise.
  return Number.isInteger(mb) ? `${mb}MB` : `${mb.toFixed(1)}MB`;
};

/** A `string` value is an existing remote URL; a `File` is a fresh pick. */
const isRemoteUrl = (v: File | string | null | undefined): v is string => typeof v === "string";

/* ============================================================================
 * Component
 * ========================================================================= */

export const ImageUploader = React.forwardRef<HTMLDivElement, ImageUploaderProps>(
  (
    {
      variant = "dropzone",
      shape = "square",
      size = "md",
      value,
      defaultValue = null,
      onChange,
      accept = DEFAULT_ACCEPT,
      maxSizeBytes = DEFAULT_MAX_SIZE,
      onReject,
      upload,
      onUploadingChange,
      title = "Drop an image here",
      subtitle = "or click to browse",
      hint,
      name,
      "aria-label": ariaLabel = "Upload image",
      renderPreview,
      disabled = false,
      className,
      ...rest
    },
    forwardedRef,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = reduced === false || reduced === null;

    const autoId = React.useId();
    const inputId = `image-uploader-${autoId}`;
    const errorId = `${inputId}-error`;

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    // Hybrid controlled / uncontrolled file state. In controlled mode `value`
    // wins; uncontrolled mode seeds from `defaultValue` and holds the full
    // value shape (`File` for a fresh pick, `string` for an existing remote
    // URL) so a string default still resolves through the preview path.
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = React.useState<File | string | null>(
      isControlled ? null : defaultValue,
    );
    const currentValue = isControlled ? value : internalValue;

    // Object-URL preview for a picked File — created + revoked HERE so the
    // consumer never leaks a blob. Revokes on unmount and on every replace
    // (the cleanup return + the dependency on the File identity).
    const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
    const fileForUrl = currentValue instanceof File ? currentValue : null;
    React.useEffect(() => {
      if (!fileForUrl) {
        setObjectUrl(null);
        return;
      }
      const url = URL.createObjectURL(fileForUrl);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [fileForUrl]);

    // Upload lifecycle. `uploadedUrl` is the resolved remote URL once `upload`
    // settles; while pending the object-URL preview stays visible underneath.
    const [status, setStatus] = React.useState<ImageUploaderStatus>("idle");
    const [uploadedUrl, setUploadedUrl] = React.useState<string | null>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    // Each upload run is tagged so a stale resolve (the user replaced the file
    // mid-flight) can't clobber the newer preview.
    const uploadRunRef = React.useRef(0);

    const notifyUploading = React.useCallback(
      (next: ImageUploaderStatus) => {
        setStatus(next);
        onUploadingChange?.(next === "uploading");
      },
      [onUploadingChange],
    );

    // Resolve the source the preview should render. Priority: an uploaded URL
    // (persistence done) → the live object URL (fresh File) → a remote-URL
    // value (existing saved image). A blob preview is never clobbered by a
    // remote value arriving mid-upload because `objectUrl` wins over the
    // string branch.
    const previewSrc = uploadedUrl ?? objectUrl ?? (isRemoteUrl(currentValue) ? currentValue : null);
    const hasPreview = !!previewSrc;
    const isUploading = status === "uploading";

    const acceptAttr = React.useMemo(() => accept.join(","), [accept]);
    const maxSizeLabel = React.useMemo(() => formatMaxSize(maxSizeBytes), [maxSizeBytes]);

    const validate = React.useCallback(
      (file: File): ImageRejection | null => {
        if (accept.length > 0 && !accept.includes(file.type)) {
          const labels = accept
            .map((t) => t.split("/")[1]?.replace("+xml", "").toUpperCase())
            .filter(Boolean)
            .join(", ");
          return {
            file,
            code: "file-invalid-type",
            message: `That file type is not supported. Please upload ${labels || "an image"}.`,
          };
        }
        if (file.size > maxSizeBytes) {
          return {
            file,
            code: "file-too-large",
            message: `Image file must be ${maxSizeLabel} or smaller. Please choose a smaller file.`,
          };
        }
        return null;
      },
      [accept, maxSizeBytes, maxSizeLabel],
    );

    const commitFile = React.useCallback(
      (file: File) => {
        // Reset any prior upload result so the new file's object-URL preview
        // shows immediately (the ProfileHero "instant local preview").
        setUploadedUrl(null);
        setErrorMessage(null);

        if (!isControlled) setInternalValue(file);
        onChange?.(file);

        if (!upload) {
          notifyUploading("idle");
          return;
        }

        const runId = ++uploadRunRef.current;
        notifyUploading("uploading");
        upload(file)
          .then((url) => {
            if (uploadRunRef.current !== runId) return; // superseded
            setUploadedUrl(url);
            notifyUploading("done");
          })
          .catch((err: unknown) => {
            if (uploadRunRef.current !== runId) return;
            setErrorMessage(err instanceof Error ? err.message : "Upload failed. Please try again.");
            notifyUploading("error");
          });
      },
      [isControlled, onChange, upload, notifyUploading],
    );

    // Take the first file off a FileList, validate it, and either commit it or
    // report the rejection outward. Multiple files collapse to the first (the
    // component is single-file by contract); extras beyond the first are
    // ignored, not rejected.
    const handleFiles = React.useCallback(
      (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        const rejection = validate(file);
        if (rejection) {
          setErrorMessage(rejection.message);
          notifyUploading("error");
          onReject?.([rejection]);
          return;
        }
        commitFile(file);
      },
      [validate, commitFile, onReject, notifyUploading],
    );

    const openFileDialog = React.useCallback(() => {
      if (disabled || isUploading) return;
      inputRef.current?.click();
    }, [disabled, isUploading]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      // Reset so re-picking the SAME file fires `change` again (the bug every
      // hand-rolled copy fixes with `e.target.value = ""`).
      e.target.value = "";
    };

    const remove = React.useCallback(
      (e?: React.MouseEvent) => {
        e?.stopPropagation();
        uploadRunRef.current++; // cancel any in-flight upload's effect
        setUploadedUrl(null);
        setErrorMessage(null);
        notifyUploading("idle");
        if (!isControlled) setInternalValue(null);
        onChange?.(null);
        if (inputRef.current) inputRef.current.value = "";
      },
      [isControlled, onChange, notifyUploading],
    );

    /* ---- Native drag-drop. A counter survives child-element dragenter /
     * dragleave bubbling (the window-leave + nested-drag tracking react-
     * dropzone hardens). ------------------------------------------------- */
    const [isDragging, setIsDragging] = React.useState(false);
    const dragDepth = React.useRef(0);

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled || isUploading) return;
      dragDepth.current += 1;
      if (e.dataTransfer?.types?.includes("Files")) setIsDragging(true);
    };
    const handleDragOver = (e: React.DragEvent) => {
      // Required for `drop` to fire; also signals a copy cursor.
      e.preventDefault();
      if (!disabled && !isUploading && e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setIsDragging(false);
    };
    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      if (disabled || isUploading) return;
      handleFiles(e.dataTransfer?.files ?? null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openFileDialog();
      }
    };

    const hasError = status === "error";
    const isAvatar = variant === "avatar";

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    // The rendered preview, wrapped in the controlled PixelCard so it resolves
    // through a pixel-reveal (self-collapsing under reduced motion).
    const preview = hasPreview ? (
      <PixelCard
        overlay={false}
        active
        variant="muted"
        tabIndex={-1}
        className="absolute inset-0 h-full w-full rounded-[inherit] border-0"
      >
        {renderPreview ? (
          renderPreview(previewSrc as string)
        ) : (
          <Image
            src={previewSrc as string}
            alt={name ? `${name} image` : "Selected image"}
            className="absolute inset-0 h-full w-full rounded-[inherit]"
            rounded="none"
          />
        )}
      </PixelCard>
    ) : null;

    return (
      <div className={cn("flex flex-col gap-2", isAvatar && "inline-flex")}>
        {/* Native drag-drop root. A plain <div> (not motion.div) because
            motion's `onDrag*` props are pan-gesture handlers, not native HTML
            drag events — the same reason Button / EmptyState omit them. The
            subtle drag scale lives on the empty-content motion wrapper below. */}
        <div
          ref={setRefs}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-disabled={disabled || undefined}
          aria-describedby={hasError ? errorId : undefined}
          data-slot="image-uploader"
          data-variant={variant}
          data-dragging={isDragging || undefined}
          data-uploading={isUploading || undefined}
          onClick={openFileDialog}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            imageUploaderVariants({
              variant,
              shape,
              size,
              dragging: isDragging,
              hasError,
              disabled,
            }),
            !disabled && "cursor-pointer",
            className,
          )}
          {...rest}
        >
          {/* Empty ↔ preview cross-fade. */}
          <AnimatePresence initial={false} mode="popLayout">
            {hasPreview ? (
              <motion.div
                key="preview"
                className="absolute inset-0 rounded-[inherit]"
                initial={shouldAnimate ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                exit={shouldAnimate ? { opacity: 0 } : { opacity: 0 }}
                transition={transitions.surface}
              >
                {preview}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                className={cn(
                  "relative z-[1] flex flex-col items-center justify-center",
                  isAvatar ? "gap-0" : "gap-2",
                )}
                initial={shouldAnimate ? { opacity: 0 } : false}
                // Subtle inner scale on drag-enter (springs.snappy) layered on
                // the empty↔preview opacity fade; reduced motion gets opacity
                // only.
                animate={{ opacity: 1, scale: shouldAnimate && isDragging ? 1.03 : 1 }}
                exit={shouldAnimate ? { opacity: 0 } : { opacity: 0 }}
                transition={{ opacity: transitions.surface, scale: springs.snappy }}
              >
                {isAvatar ? (
                  name ? (
                    <span
                      aria-hidden
                      className={cn(
                        "font-semibold text-foreground/70",
                        size === "sm" ? "text-base" : size === "lg" ? "text-2xl" : "text-lg",
                      )}
                    >
                      {initialOf(name)}
                    </span>
                  ) : (
                    <ImagePlus
                      aria-hidden
                      className={cn(size === "sm" ? "size-4" : size === "lg" ? "size-7" : "size-5")}
                    />
                  )
                ) : (
                  <>
                    <span className="flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <UploadCloud aria-hidden className="size-5" />
                    </span>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-foreground">{title}</p>
                      {subtitle ? (
                        <p className="text-xs text-muted-foreground">{subtitle}</p>
                      ) : null}
                    </div>
                    {hint ? (
                      <p className="text-[11px] text-muted-foreground/80">{hint}</p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground/80">
                        {accept
                          .map((t) => t.split("/")[1]?.replace("+xml", "").toUpperCase())
                          .filter(Boolean)
                          .join(", ")}{" "}
                        • Max {maxSizeLabel}
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Uploading scrim — dims + spins over whatever is showing. Matches
              CoverImageUpload's `opacity-50 pointer-events-none`. */}
          <AnimatePresence>
            {isUploading ? (
              <motion.div
                key="uploading"
                className="absolute inset-0 z-[2] flex items-center justify-center rounded-[inherit] bg-background/60 backdrop-blur-[1px]"
                initial={shouldAnimate ? { opacity: 0 } : false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={transitions.hover}
              >
                <Spinner size={isAvatar ? "default" : "lg"} aria-label="Uploading image" />
              </motion.div>
            ) : null}
          </AnimatePresence>

          {/* Avatar overlay affordance: a small change/remove control set that
              fades in on hover / focus once an image exists. The dropzone
              variant uses the dedicated button row below instead. */}
          {isAvatar && hasPreview && !isUploading && !disabled ? (
            <div
              className={cn(
                "absolute inset-0 z-[3] flex items-center justify-center gap-1 rounded-[inherit] bg-foreground/45 text-background",
                "opacity-0 transition-opacity duration-180 ease-smooth-out",
                "group-hover/uploader:opacity-100 group-focus-within/uploader:opacity-100",
                "motion-reduce:transition-none",
              )}
            >
              <RefreshCw aria-hidden className="size-4" />
            </div>
          ) : null}

          {/* Hidden native input — the click / keyboard path. Kept inside the
              labelled control; reset to "" on every select. */}
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={acceptAttr}
            disabled={disabled}
            onChange={handleInputChange}
            onClick={(e) => e.stopPropagation()}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
          />
        </div>

        {/* Dropzone action row + inline error. The avatar variant reports the
            error compactly under the well too. */}
        {variant === "dropzone" && hasPreview && !disabled ? (
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={openFileDialog}
              disabled={isUploading}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium",
                "text-foreground transition-colors duration-180 ease-smooth-out hover:bg-muted",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                "disabled:opacity-50",
              )}
            >
              <RefreshCw aria-hidden className="size-3.5" />
              Replace
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={isUploading}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium",
                "text-muted-foreground transition-colors duration-180 ease-smooth-out hover:text-destructive hover:bg-destructive/5",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                "disabled:opacity-50",
              )}
            >
              <X aria-hidden className="size-3.5" />
              Remove
            </button>
          </div>
        ) : null}

        {/* Avatar remove affordance: a small text button under the well so the
            control stays reachable without relying on hover. */}
        {isAvatar && hasPreview && !disabled && !isUploading ? (
          <button
            type="button"
            onClick={remove}
            className={cn(
              "text-[11px] font-medium text-muted-foreground transition-colors hover:text-destructive",
              "focus-visible:outline-none focus-visible:underline",
            )}
          >
            Remove
          </button>
        ) : null}

        {hasError && errorMessage ? (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {errorMessage}
          </p>
        ) : null}
      </div>
    );
  },
);
ImageUploader.displayName = "ImageUploader";
